import { fromJS } from 'immutable'

import { isRowAveragesUid } from '@aytm/app/insights/dashboard/lib/config'
import { resort, reorder } from '@aytm/app/insights/dashboard/lib/util'
import { combineSplitGroups, getUidsSet } from '@aytm/app/insights/dashboard/helpers/itemsTransformation'
import { isBenchmark } from './checks'
import { forLetterStore } from '@aytm/app/insights/dashboard/lib/letters'

import { ItemType, PresetsType, SubjectsType, AttributeRow } from '@aytm/app/insights/dashboard/models/types.ClientDTO'
import { BlockModel, ItemListModel } from '@aytm/app/insights/dashboard/models/BlockModel'
import { InsightsDashboardPoolState } from '@aytm/app/insights/dashboard/duckness/ducks/types'

import { captionsFactory, getCurrentMethod } from './attributes'
import { getCurrentBlock, updateCurrentBlock, updateCurrentData } from './requests'
import { hasCellDataFactory, type HasCellData } from './values'

type ProcessItemsParams = {
  blockUID?: string | null
  subjectType?: string | null
  attribute?: string | null
  preset?: string | null
  split?: string | null
  presets?: PresetsType
  subjects?: SubjectsType
  items?: ItemType[]
  getLetter?: (uid: string) => string
}

export type UpdateHiddenItemsPayload = {
  hidden?: boolean
  preset?: string
}

export type ItemReplacePayload = {
  from: string
  to: string
}

const updateHiddenItemsList = (hiddenItems: string[], uids: string[], hidden: boolean): void => {
  for (const uid of uids) {
    if (hidden && !hiddenItems.includes(uid)) {
      hiddenItems.push(uid)
    } else if (!hidden && hiddenItems.includes(uid)) {
      hiddenItems.splice(hiddenItems.indexOf(uid), 1)
    }
  }
}

const captionsHasData = (captions: AttributeRow[], uids: string[], hasCellData: HasCellData): boolean => {
  for (const uid of uids) {
    for (const caption of captions) {
      if (hasCellData(uid, caption.attribute, caption.metrics, caption.defaultMetrics)) {
        return true
      }
    }
  }
  return false
}

export const updateHiddenItems = (
  state: InsightsDashboardPoolState,
  { hidden, preset }: UpdateHiddenItemsPayload = {}
): InsightsDashboardPoolState => {
  let resultState = state

  const block = getCurrentBlock(state)

  const items = block.getItemsArray() || []
  const currentPreset = preset || block.get<string>('currentPreset') || 'All'
  const hideBlankColumns = hidden || block.get<boolean>('hideBlankColumns') || false
  const hiddenItems = state.currentData?.hiddenItems || []

  if (0 === items.length) {
    return state
  }

  if ('All' === currentPreset && hideBlankColumns) {
    const data = state.currentData?.data || {}
    const sections = block.getSections()
    const trackTarget = block.get<string>('trackTarget') || 'entity'
    const currentSplit = block.get<string>('currentSplit') || null
    const attributes = state.currentData?.attributes || {}
    const dataMetrics = state.currentData?.dataMetrics || {}

    const shownAttributes = captionsFactory({ attributes, dataMetrics, sections, trackTarget })({
      showSections: false,
      showMetrics: true,
      showMetadata: false,
      showCollapsed: true,
      showUnique: true
    }) as AttributeRow[]

    if (0 === shownAttributes.length) {
      return state
    }

    const hasCellData = hasCellDataFactory({ data, attributes })
    const nextHiddenItems = [...hiddenItems]

    if (currentSplit) {
      // Hide totally empty waves
      const waveGroups = items.reduce(
        (acc, cur) => {
          acc[cur.waveUid] = acc[cur.waveUid] || []
          acc[cur.waveUid].push(cur.uid)
          return acc
        },
        {} as Record<string, string[]>
      )
      for (const uids of Object.values(waveGroups)) {
        const isHidden = !captionsHasData(shownAttributes, uids, hasCellData)
        updateHiddenItemsList(nextHiddenItems, uids, isHidden)
      }
    } else {
      for (const { uid } of items) {
        const isHidden = !captionsHasData(shownAttributes, [uid], hasCellData)
        updateHiddenItemsList(nextHiddenItems, [uid], isHidden)
      }
    }

    resultState = updateCurrentData(resultState, { hiddenItems: nextHiddenItems })
  } else {
    if (hiddenItems.length) {
      resultState = updateCurrentData(resultState, { hiddenItems: [] })
    }
  }

  return resultState
}

const correctItem = (
  uid: string,
  items: ItemType[],
  subjects: SubjectsType,
  getLetter: (uid: string) => string
): ItemType => {
  const found = (items || []).find(v => uid === v.uid)
  const pinned = found?.pinned || isRowAveragesUid(uid) || false
  const letter = getLetter(uid)
  const waveUid = subjects[uid]?.waveUid || null
  return { uid, letter, pinned, waveUid }
}

const lettersBuilder = (block: BlockModel) => {
  const letterStore = block?.getObject('letterStore') || {}
  const getSubjectLetter = forLetterStore(letterStore).getSubjectLetter
  return (uid: string) => getSubjectLetter(uid, block)
}

const getProcessItemsParams = (
  state: InsightsDashboardPoolState,
  { blockUID, subjectType, attribute, preset, split, presets, subjects, items }: ProcessItemsParams = {}
) => {
  const sourceBlockUID = blockUID || state?.currentSchema?.get('currentBlockUID') || null
  const sourceBlock = state?.currentSchema?.getIn<BlockModel>(['blocks', sourceBlockUID])
  const sourceSubjectType = subjectType || sourceBlock?.getSubjectType() || null
  const sourcePreset = preset || sourceBlock?.get('currentPreset') || 'All'
  const sourceSplit = split || sourceBlock?.get('currentSplit') || null
  const sourcePresets = presets || state?.currentData?.presets || {}
  const sourceSortByAttribute = attribute || sourceBlock?.get<string>('sortByAttribute') || null
  const sourceAttribute = sourcePresets[sourcePreset]?._ ? '_' : sourceSortByAttribute
  const sourceSubjects = subjects || state?.currentData?.subjects || {}
  const sourceItems = (items || sourceBlock?.getItemsArray()) ?? []
  const getLetter = lettersBuilder(sourceBlock)
  return {
    blockUID: sourceBlockUID,
    subjectType: sourceSubjectType,
    attribute: sourceAttribute,
    preset: sourcePreset,
    split: sourceSplit,
    presets: sourcePresets,
    subjects: sourceSubjects,
    items: sourceItems,
    getLetter
  }
}

const processConceptItems = ({
  attribute,
  preset,
  split,
  presets,
  subjects,
  items,
  getLetter
}: ProcessItemsParams = {}): ItemListModel => {
  // Process items
  if (preset && 'custom' !== preset) {
    // Get items from preset
    const attributePreset = presets[preset]?.[attribute] || []
    let newItems = attributePreset.map(uid => correctItem(uid, items, subjects, getLetter))
    // New preset may not contains pinned items.
    // To preserve pinned items move them to new generated preset
    if (!split) {
      const pinnedItems = items.filter(v => v?.pinned)
      for (const pinnedItem of pinnedItems) {
        if (!newItems.some(v => pinnedItem.uid === v.uid)) {
          newItems.push(pinnedItem)
        }
      }
    }
    // Make split groups if needed
    newItems = split ? combineSplitGroups(subjects, newItems) : newItems
    // Sort items in previous order
    newItems = newItems.sort((a, b) => items.findIndex(v => a.uid === v.uid) - items.findIndex(v => b.uid === v.uid))
    // Return new block items
    return fromJS(newItems) as ItemListModel
  } else {
    // Process custom preset
    // Get wave uids
    const waveUids = items.reduce((acc, cur) => {
      const waveUid = cur.waveUid ? cur.waveUid : subjects?.[cur.uid]?.waveUid
      if (waveUid && !acc.includes(waveUid)) {
        acc.push(waveUid)
      }
      return acc
    }, [] as string[])
    // Create new items array
    let newItems = Object.keys(subjects)
      .map(uid => correctItem(uid, items, subjects, getLetter))
      .filter(
        item =>
          waveUids.includes(item.waveUid) || (isBenchmark({ uid: item.uid }) && items.some(v => v.uid === item.uid))
      )
    // Make split groups if needed
    newItems = split ? combineSplitGroups(subjects, newItems) : newItems
    // Sort items in previous order
    newItems = newItems.sort((a, b) => items.findIndex(v => a.uid === v.uid) - items.findIndex(v => b.uid === v.uid))
    // Return new block items
    return fromJS(newItems) as ItemListModel
  }
}

const processWaveItems = ({ preset, split, subjects, items, getLetter }: ProcessItemsParams = {}): ItemListModel => {
  // Collect wave uids
  const waveUids =
    'custom' === preset
      ? items.reduce((acc, cur) => {
          const waveUid = cur.waveUid ? cur.waveUid : subjects?.[cur.uid]?.waveUid
          if (waveUid && !acc.includes(waveUid)) {
            acc.push(waveUid)
          }
          return acc
        }, [] as string[])
      : Object.values(subjects).reduce((acc, cur) => {
          const waveUid = cur.waveUid
          if (waveUid && !acc.includes(waveUid)) {
            acc.push(waveUid)
          }
          return acc
        }, [] as string[])
  // Create new items array
  let newItems = Object.keys(subjects)
    .map(uid => correctItem(uid, items, subjects, getLetter))
    .filter(
      item => waveUids.includes(item.waveUid) || (isBenchmark({ uid: item.uid }) && items.some(v => v.uid === item.uid))
    )
  // Make split groups if needed
  newItems = split ? combineSplitGroups(subjects, newItems) : newItems
  // Return new block items
  return fromJS(newItems) as ItemListModel
}

export const processItems = (
  state: InsightsDashboardPoolState,
  { blockUID, subjectType, attribute, preset, split, presets, subjects, items }: ProcessItemsParams = {}
): ItemListModel => {
  const params = getProcessItemsParams(state, {
    blockUID,
    subjectType,
    attribute,
    preset,
    split,
    presets,
    subjects,
    items
  })
  return 'wave' === params.subjectType ? processWaveItems(params) : processConceptItems(params)
}

export const reorderItems = (state: InsightsDashboardPoolState): InsightsDashboardPoolState => {
  const block = getCurrentBlock(state)
  let items = block?.getItemsArray() || []
  const currentSplit = block?.get<string>('currentSplit') || null
  const subjectType = block?.getSubjectType() || null
  const sortOrder = block?.get<string>('sortOrder') || 'asc'
  const subjects = state.currentData?.subjects || {}
  items = currentSplit && subjects ? combineSplitGroups(subjects, items) : items
  const getLetter = lettersBuilder(block)
  const data = state.currentData?.data || {}
  const sortByAttribute = block?.get<string>('sortByAttribute')
  const currentMethod = getCurrentMethod(state)
  const shouldUseResort = 'concept' === subjectType && !currentSplit
  const fixPinned = 'concept' === subjectType && !currentSplit
  items = shouldUseResort
    ? resort({ items, data, sortByKey: sortByAttribute, sortByMethod: currentMethod, sortOrder })
    : reorder({ items, subjects, separate: fixPinned })
  items = items.map<ItemType>((item: ItemType) => {
    if (!item.letter) {
      item.letter = getLetter(item.uid)
    }
    return item
  })
  return updateCurrentBlock(state, { items })
}

export const itemAdd = (state: InsightsDashboardPoolState, uid: string): InsightsDashboardPoolState => {
  let resultState = state
  const block = getCurrentBlock(state)
  const getLetter = lettersBuilder(block)
  const items = block?.getItemsArray()
  const subjects = state.currentData?.subjects || {}
  const itemIndex = items.findIndex(v => uid === v.uid) ?? -1
  if (subjects[uid]) {
    const newItems = items.slice()
    const letter = getLetter(uid)
    const waveUid = subjects[uid].waveUid || null
    if (isRowAveragesUid(uid)) {
      if (itemIndex < 0) {
        newItems.unshift({ uid, letter, pinned: true, waveUid, new: false })
      }
      resultState = updateCurrentBlock(resultState, { items: newItems, showRowAverages: true })
    } else {
      if (itemIndex < 0) {
        const uids = Object.entries(subjects).reduce((acc, [subjectUid, subject]) => {
          if (subject.waveUid === waveUid) {
            acc.push(subjectUid)
          }
          return acc
        }, [] as string[])
        for (const uid of uids) {
          newItems.push({ uid, letter, pinned: false, waveUid, new: true })
        }
      }
      const allUids = new Set(state.currentData.presets.All._ || [])
      allUids.delete('avg')
      const itemsUids = getUidsSet(newItems)
      itemsUids.delete('avg')
      const currentPreset = allUids.size === itemsUids.size ? 'All' : 'custom'
      resultState = updateCurrentBlock(resultState, { items: newItems, sortOrder: null, currentPreset })
    }
  }
  return resultState
}

export const itemDelete = (state: InsightsDashboardPoolState, uid: string): InsightsDashboardPoolState => {
  let resultState = state
  const block = getCurrentBlock(state)
  const items = block?.getItemsArray() || []
  const subjects = state.currentData?.subjects || {}
  const waveIndex = subjects[uid]?.waveIndex || 0
  const deleteUids = waveIndex
    ? Object.values(subjects).reduce(
        (acc, cur) => (waveIndex === cur.waveIndex ? acc.concat(cur.uid) : acc),
        [] as string[]
      )
    : [uid]
  const newItems = items.filter(item => !deleteUids.includes(item.uid))
  resultState = isRowAveragesUid(uid)
    ? updateCurrentBlock(resultState, { items: newItems, showRowAverages: false })
    : updateCurrentBlock(resultState, { items: newItems, currentPreset: 'custom' })
  return resultState
}

export const itemReplaceExternal = (
  state: InsightsDashboardPoolState,
  { from: invisibleSourceUid, to: visibleTargetUid }: ItemReplacePayload
): InsightsDashboardPoolState => {
  const block = getCurrentBlock(state)
  const getLetter = lettersBuilder(block)
  const items = block?.getItemsArray()
  const visibleItemIndex = items.findIndex(v => visibleTargetUid === v.uid) ?? -1

  const subjects = state.currentData?.subjects || {}

  if (!items?.[visibleItemIndex] || !subjects?.[invisibleSourceUid]) {
    return state
  }

  const newItems = items.slice()
  const letter = getLetter(invisibleSourceUid)
  const pinned = isRowAveragesUid(invisibleSourceUid) ? true : items[visibleItemIndex].pinned
  const waveUid = subjects[invisibleSourceUid].waveUid || null
  newItems[visibleItemIndex] = {
    uid: invisibleSourceUid,
    letter,
    pinned,
    waveUid
  }
  return updateCurrentBlock(state, {
    items: newItems,
    currentPreset: 'custom',
    sortOrder: 'manual',
    sortOrderManualEnabled: true
  })
}

export const itemReplaceVisible = (
  state: InsightsDashboardPoolState,
  { from, to }: ItemReplacePayload
): InsightsDashboardPoolState => {
  const block = getCurrentBlock(state)
  const items = block?.getItemsArray()
  const fromIndex = items.findIndex(v => from === v.uid) ?? -1
  const toIndex = items.findIndex(v => to === v.uid) ?? -1
  if (fromIndex < 0 || toIndex < 0) {
    // do nothing; this can't happen due to preconditions
    return state
  }
  // Replace items preserving pin's positions
  ;[items[fromIndex].pinned, items[toIndex].pinned] = [items[toIndex]?.pinned, items[fromIndex]?.pinned]
  ;[items[fromIndex], items[toIndex]] = [items[toIndex], items[fromIndex]]
  // Update items and sort by attribute in block schema
  return updateCurrentBlock(state, {
    items: [...items] /*, currentPreset: 'custom'*/,
    sortOrder: 'manual',
    sortOrderManualEnabled: true
  })
}

export type ItemPinPayload = {
  uid: string
  pinned: boolean
}

export const itemPin = (
  state: InsightsDashboardPoolState,
  { uid, pinned }: ItemPinPayload
): InsightsDashboardPoolState => {
  const block = getCurrentBlock(state)
  const items = block?.getItemsArray()
  const currentPreset = block?.get<string>('currentPreset') || null
  const sortByAttribute = block?.get<string>('sortByAttribute') || null
  const presets = state.currentData?.presets || {}
  let resultState = state
  const itemIndex = items.findIndex(v => uid === v.uid) ?? -1
  if (itemIndex >= 0) {
    if (isRowAveragesUid(uid)) {
      resultState = pinned ? resultState : itemDelete(resultState, uid)
    } else {
      const newItems = items.slice()
      newItems[itemIndex].pinned = pinned
      resultState = updateCurrentBlock(resultState, { items: newItems })
      if (!pinned) {
        const presetUids = presets?.[currentPreset]?.[sortByAttribute] || null
        if (presetUids && !presetUids.includes(uid)) {
          resultState = updateCurrentBlock(resultState, { currentPreset: 'custom' })
        }
      }
    }
  }
  return resultState
}

export const presetWaveUids = (state: InsightsDashboardPoolState, preset: string, items: ItemType[]): string[] => {
  const block = getCurrentBlock(state)
  const subjects = state.currentData?.subjects || {}
  const presets = state.currentData?.presets || {}
  if ('All' === preset) {
    return null
  } else if (!preset || 'custom' === preset) {
    return (items || []).reduce<string[]>((acc, cur) => {
      const waveUid = cur.waveUid ? cur.waveUid : subjects?.[cur.uid]?.waveUid
      if (waveUid && !isRowAveragesUid(waveUid) && !acc.includes(waveUid)) {
        acc.push(waveUid)
      }
      return acc
    }, [])
  } else {
    const attribute = 'Recent 25' === preset ? '_' : block?.get<string>('sortByAttribute') || null
    return (presets?.[preset]?.[attribute] || []).reduce<string[]>((acc, cur) => {
      const waveUid = subjects?.[cur]?.waveUid
      if (waveUid && !acc.includes(waveUid)) {
        acc.push(waveUid)
      }
      return acc
    }, [])
  }
}

export const itemsOrderChanged = (itemsA: ItemType[], itemsB: ItemType[]): boolean => {
  if ((itemsA?.length || 0) !== (itemsB?.length || 0)) {
    return true
  }
  for (let i = 0; i < itemsA?.length || 0; i++) {
    if (
      itemsA[i].uid !== itemsB[i].uid ||
      itemsA[i].pinned !== itemsB[i].pinned ||
      itemsA[i].letter !== itemsB[i].letter
    ) {
      return true
    }
  }
  return false
}

export const itemsSwapAlowed = (items: ItemType[], from: number, to: number): boolean => {
  const swapProhibited =
    (!items[from].pinned && isRowAveragesUid(items[to]?.uid)) ||
    (isRowAveragesUid(items[from]?.uid) && !items[to]?.pinned)
  return !swapProhibited
}
